// System-theme-driven favicon swap — the CHROMIUM half of the strategy.
// Chromium does NOT honor the `media` attribute on `<link rel="icon">`
// (open issue since 2012, crbug.com/156722), so for it we flip the favicon
// from JavaScript: attach a single matchMedia listener at app boot and
// rewrite the icon href on every change.
//
// Safari & Firefox are handled separately by the media-scoped <link
// rel="icon"> tags in public/index.html — crucially, iOS/iPadOS Safari
// ignores JavaScript-injected favicon changes entirely, so the JS path
// below never takes effect there and the static media links are what make
// the swap work on iPad. Both paths resolve to the same choice, so the two
// approaches can coexist without fighting.
//
// Intentionally decoupled from the in-app theme toggle — the favicon should
// track the BROWSER chrome, not whatever Light/Dark preference the user
// picked inside the app.

const DARK_QUERY = "(prefers-color-scheme: dark)";

const FAVICON_LIGHT = {
  href: "/favicon-black.png",
  type: "image/png",
};

const FAVICON_DARK = {
  href: "/favicon-white.svg",
  type: "image/svg+xml",
};

const apply = (isDark: boolean) => {
  const variant = isDark ? FAVICON_DARK : FAVICON_LIGHT;
  // Strip every existing icon link and append a fresh one. Chrome is
  // unreliable about re-fetching the favicon when only the `href` of
  // an existing tag changes — removing and re-adding the element
  // forces a re-evaluation.
  document
    .querySelectorAll<HTMLLinkElement>('link[rel="icon"]')
    .forEach((link) => link.remove());
  const link = document.createElement("link");
  link.rel = "icon";
  link.type = variant.type;
  link.href = variant.href;
  document.head.appendChild(link);
};

const init = () => {
  if (typeof window === "undefined" || !window.matchMedia) return;
  const mq = window.matchMedia(DARK_QUERY);
  apply(mq.matches);
  const onChange = () => apply(mq.matches);
  // Older Safari uses the legacy addListener / removeListener API.
  if (mq.addEventListener) mq.addEventListener("change", onChange);
  else mq.addListener(onChange);
};

init();

export {};
